// src/pages/Orders/NewOrder.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { collection, onSnapshot, query, serverTimestamp, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { logOrderCreated } from '../../utils/orderActivityLogger';
import { logOrderCreated as logSimplifiedOrderCreated } from '../../utils/simplifiedActivityLogger';
import { useAuth } from '../../context/AuthContext';
import useCustomerPricing from '../../hooks/useCustomerPricing';
import { generateOrderPDF } from '../../utils/pdfGenerator';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

// --- Aktivite Kaydı Yardımcı Fonksiyonu ---
const logActivity = async (batch, companyId, user, action, details = {}) => {
    if (!companyId || !user) return;
    const logRef = doc(collection(db, 'companies', companyId, 'activityLog'));
    batch.set(logRef, {
        userId: user.uid,
        userEmail: user.email,
        action,
        details,
        timestamp: serverTimestamp()
    });
};

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '24px',
        maxWidth: '1200px',
        margin: '0 auto',
        color: '#1e293b'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0
    },
    subtitle: {
        color: '#64748b',
        marginTop: '4px',
        fontSize: '14px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#64748b',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        borderRadius: '6px',
        transition: 'all 0.2s',
        background: '#f1f5f9'
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        border: '1px solid #e2e8f0',
        padding: '24px',
        marginBottom: '24px'
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#0f172a',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        position: 'relative'
    },
    label: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#475569'
    },
    input: {
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        color: '#0f172a',
        width: '100%',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        ':focus': {
            borderColor: '#3b82f6',
            boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
        }
    },
    select: {
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        color: '#0f172a',
        width: '100%',
        background: 'white',
        outline: 'none'
    },
    textarea: {
        padding: '12px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        fontSize: '14px',
        color: '#0f172a',
        width: '100%',
        minHeight: '80px',
        resize: 'vertical',
        fontFamily: 'inherit'
    },
    suggestions: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        marginTop: '4px',
        maxHeight: '240px',
        overflowY: 'auto',
        zIndex: 50,
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    },
    suggestionItem: {
        padding: '10px 12px',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#334155',
        transition: 'background 0.1s'
    },
    button: {
        padding: '10px 20px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
    },
    primaryButton: {
        background: '#3b82f6',
        color: 'white',
        boxShadow: '0 2px 4px rgba(59, 130, 246, 0.1)',
        ':hover': { background: '#2563eb' }
    },
    addButton: {
        background: '#10b981',
        color: 'white',
        height: '42px',
        ':hover': { background: '#059669' }
    },
    deleteButton: {
        background: '#fee2e2',
        color: '#ef4444',
        padding: '6px',
        borderRadius: '6px',
        border: 'none',
        cursor: 'pointer',
        transition: 'background 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '28px',
        height: '28px'
    },
    tableContainer: {
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        overflow: 'hidden',
        marginTop: '24px'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px'
    },
    th: {
        background: '#f8fafc',
        padding: '12px 16px',
        textAlign: 'left',
        color: '#64748b',
        fontWeight: '600',
        fontSize: '12px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderBottom: '1px solid #e2e8f0'
    },
    td: {
        padding: '12px 16px',
        borderBottom: '1px solid #e2e8f0',
        color: '#334155',
        verticalAlign: 'middle'
    },
    totalsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '24px'
    },
    totalsBox: {
        width: '320px',
        background: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e2e8f0'
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px',
        fontSize: '14px',
        color: '#64748b'
    },
    grandTotal: {
        borderTop: '1px solid #cbd5e1',
        paddingTop: '12px',
        marginTop: '12px',
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a'
    },
    vatInput: {
        width: '60px',
        padding: '4px 8px',
        borderRadius: '4px',
        border: '1px solid #cbd5e1',
        textAlign: 'right',
        fontSize: '13px'
    }
};

function NewOrder() {
    const { currentUser, userData, canViewPrices } = useAuth();
    const { customerPrices, getCustomerProductPrice, getEffectivePrice } = useCustomerPricing(userData?.companyId);
    const navigate = useNavigate();

    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [currencyRates, setCurrencyRates] = useState({ USD: 0 });

    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [customerNameInput, setCustomerNameInput] = useState('');

    const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
    const [lineItems, setLineItems] = useState([]);
    const [vatRate, setVatRate] = useState(0.20);
    const [discountRate, setDiscountRate] = useState(0);
    const [orderNote, setOrderNote] = useState('');
    const [shippingCost, setShippingCost] = useState(0);
    const [orderCurrency, setOrderCurrency] = useState('TRY');

    const [currentItem, setCurrentItem] = useState({ productId: '', productName: '', quantity: 1, price: 0, availableStock: 0 });

    const exchangeRate = orderCurrency === 'USD' ? currencyRates.USD : 1;

    // Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                handleSaveOrder();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedCustomerId, lineItems, orderDate, discountRate, vatRate, shippingCost, orderCurrency, exchangeRate]);

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;
        const unsubCustomers = onSnapshot(query(collection(db, 'companies', companyId, 'customers')), snap => setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
        const unsubProducts = onSnapshot(query(collection(db, 'companies', companyId, 'products')), snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));

        const companyRef = doc(db, 'companies', companyId);
        const unsubCompany = onSnapshot(companyRef, (docSnap) => {
            if (docSnap.exists()) {
                setCompanyInfo(docSnap.data());
                if (docSnap.data().currencySettings) {
                    setCurrencyRates(docSnap.data().currencySettings);
                }
            }
        });

        return () => { unsubCustomers(); unsubProducts(); unsubCompany(); };
    }, [userData]);

    const handleCustomerSelect = (customer) => {
        setCustomerNameInput(customer.name);
        setSelectedCustomerId(customer.id);
    };

    const handleProductSelect = (product) => {
        // Check if this product is already in the order
        const existingItem = lineItems.find(item => item.productId === product.id);

        if (existingItem) {
            // If product is already in order, pre-fill with existing values
            setCurrentItem({
                productId: product.id,
                productName: product.name,
                quantity: 1, // Default to 1 for adding more
                price: existingItem.price,
                isCustomPrice: existingItem.isCustomPrice,
                priceLabel: existingItem.priceLabel,
                defaultPrice: existingItem.defaultPrice || product.salePrice || 0,
                availableStock: product.quantity || 0,
            });
        } else {
            // Get effective price considering product-level > group+category > default
            const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
            const effectivePrice = getEffectivePrice(
                selectedCustomerId, 
                product.id, 
                product, 
                selectedCustomer?.customerGroupId
            );

            setCurrentItem({
                productId: product.id,
                productName: product.name,
                quantity: 1,
                price: effectivePrice.price,
                isCustomPrice: effectivePrice.type !== 'default',
                priceLabel: effectivePrice.label,
                defaultPrice: product.salePrice || 0,
                availableStock: product.quantity || 0,
            });
        }
    };

    const handleAddItem = () => {
        if (!currentItem.productId || currentItem.quantity <= 0) {
            toast.error("Lütfen geçerli bir ürün seçin ve miktar girin.");
            return;
        }

        // Check if the product already exists in the order
        const existingItemIndex = lineItems.findIndex(item => item.productId === currentItem.productId);

        if (existingItemIndex !== -1) {
            // Product already exists, ask user if they want to update the quantity
            const existingItem = lineItems[existingItemIndex];
            const newQuantity = existingItem.quantity + currentItem.quantity;
            const shouldUpdate = window.confirm(
                `"${currentItem.productName}" ürünü zaten siparişte mevcut. ` +
                `Mevcut miktar: ${existingItem.quantity}, Eklenen miktar: ${currentItem.quantity}. ` +
                `Toplam miktar ${newQuantity} olarak güncellensin mi?`
            );

            if (shouldUpdate) {
                // Update the existing item's quantity and total
                const updatedItems = [...lineItems];
                updatedItems[existingItemIndex] = {
                    ...existingItem,
                    quantity: newQuantity,
                    total: newQuantity * existingItem.price
                };
                setLineItems(updatedItems);
            }
        } else {
            // Product doesn't exist, add it as a new item
            setLineItems(prev => [...prev, {
                ...currentItem,
                total: currentItem.quantity * currentItem.price
            }]);
        }

        // Reset the current item form
        setCurrentItem({ productId: '', productName: '', quantity: 1, price: 0, availableStock: 0 });
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...lineItems];
        const item = newItems[index];
        item[field] = Number(value);
        item.total = item.quantity * item.price;
        setLineItems(newItems);
    };

    const handleRemoveItem = (index) => {
        const newItems = [...lineItems];
        newItems.splice(index, 1);
        setLineItems(newItems);
    };

    const subTotal = lineItems.reduce((acc, item) => acc + item.total, 0);
    const discountAmount = subTotal * (discountRate / 100);
    const discountedSubTotal = subTotal - discountAmount;
    const vatAmount = discountedSubTotal * vatRate;
    const grandTotal = discountedSubTotal + vatAmount + Number(shippingCost || 0);
    // Calculate total product quantity
    const totalProductQuantity = lineItems.reduce((acc, item) => acc + item.quantity, 0);

    const handleSaveOrder = async () => {
        if (!selectedCustomerId || lineItems.length === 0) {
            toast.error("Lütfen bir müşteri seçin ve en az bir ürün ekleyin.");
            return;
        }

        const customer = customers.find(c => c.id === selectedCustomerId);
        const companyId = userData.companyId;
        const batch = writeBatch(db);

        try {
            const newOrderRef = doc(collection(db, 'companies', companyId, 'orders'));

            const itemsToSave = lineItems.map(item => {
                const { productName, availableStock, isCustomPrice, ...rest } = item;
                return rest;
            });

            const orderData = {
                customerId: customer.id,
                customerName: customer.name,
                customerPhone: customer.phone || '',
                customerEmail: customer.email || '',
                customerAddress: customer.address || '',
                customerCity: customer.city || '',
                customerDistrict: customer.district || '',
                orderDate: new Date(orderDate),
                lineItems: itemsToSave,
                subTotal,
                discountRate,
                discountAmount,
                discountedSubTotal,
                vatRate,
                vatAmount,
                shippingCost: Number(shippingCost || 0),
                total: grandTotal,
                currency: orderCurrency,
                exchangeRate: exchangeRate,
                totalTRY: grandTotal * exchangeRate,
                status: 'Sipariş Alındı',
                note: orderNote,
                orderNumber: newOrderRef.id.substring(0, 6).toUpperCase()
            };

            batch.set(newOrderRef, orderData);

            // Save customer-specific prices for future reference
            lineItems.forEach(item => {
                const priceRef = doc(collection(db, 'companies', companyId, 'customerPrices'));
                batch.set(priceRef, {
                    customerId: selectedCustomerId,
                    productId: item.productId,
                    price: item.price,
                    createdAt: new Date(),
                    orderId: newOrderRef.id
                });
            });

            // Log the old activity for company-wide tracking
            await logActivity(
                batch,
                companyId,
                currentUser,
                `Yeni bir sipariş oluşturdu.`,
                { orderId: newOrderRef.id, customerName: customer.name, total: grandTotal }
            );

            // Commit the batch first
            await batch.commit();

            // Then log the detailed order activity
            await logOrderCreated(companyId, newOrderRef.id, currentUser, {
                ...orderData,
                userRole: userData?.role || 'kullanıcı'
            });

            // Log the simplified activity
            await logSimplifiedOrderCreated(companyId, currentUser, {
                customerName: customer.name,
                total: grandTotal,
                currency: orderCurrency,
                lineItems: itemsToSave,
                userRole: userData?.role || 'kullanıcı'
            });

            // --- Sipariş E-postası Gönder ---
            if (customer.email) {
                try {
                    // PDF Oluştur (Base64 olarak)
                    const doc = await generateOrderPDF(
                        { ...companyInfo, companyName: companyInfo?.companyName || userData?.companyName }, // Şirket bilgileri
                        { ...orderData, id: newOrderRef.id }, // Sipariş verisi (ID ile)
                        products,
                        true, // Görsellerle
                        true, // Fiyatlarla
                        false, // Stoksuz
                        false // Kaydetme (sadece doc nesnesini al)
                    );

                    const pdfBase64 = doc.output('datauristring');

                    const sendOrderEmail = httpsCallable(functions, 'sendOrderEmailWithAttachment');
                    await sendOrderEmail({
                        email: customer.email,
                        orderId: newOrderRef.id,
                        pdfBase64: pdfBase64,
                        customerName: customer.name,
                        totalAmount: grandTotal.toFixed(2),
                        currency: orderCurrency,
                        companyId: companyId
                    });

                    console.log("Sipariş e-postası başarıyla gönderildi.");
                } catch (emailError) {
                    console.error("Sipariş e-postası gönderilirken hata:", emailError);
                    // Kullanıcıya e-posta gönderilemediğini bildirebiliriz ama sipariş kaydedildi
                }
            }

            toast.success("Sipariş başarıyla kaydedildi.");
            navigate('/siparisler');
        } catch (error) {
            console.error("Sipariş kaydedilirken hata:", error);
            toast.error("Sipariş kaydedilirken bir hata oluştu.");
        }
    };

    const filteredCustomers = customerNameInput ? customers.filter(c => c.name.toLowerCase().includes(customerNameInput.toLowerCase())) : [];

    const filteredProducts = currentItem.productName
        ? products.filter(p =>
            p.name.toLowerCase().includes(currentItem.productName.toLowerCase()) ||
            (p.barcode && p.barcode.toLowerCase().includes(currentItem.productName.toLowerCase()))
        )
        : [];

    const currencySymbol = orderCurrency === 'USD' ? '$' : '₺';

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div>
                    <h1 style={styles.title}>Yeni Sipariş Oluştur</h1>
                    <p style={styles.subtitle}>Müşteri siparişi oluşturun ve ürünleri ekleyin</p>
                </div>
                <Link to="/siparisler" style={styles.backLink}>
                    <span>←</span> Sipariş Listesine Dön
                </Link>
            </div>

            <div style={styles.card}>
                {/* Display customer-specific pricing summary at the top */}
                {selectedCustomerId && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px',
                        padding: '16px',
                        background: '#eff6ff',
                        borderRadius: '12px',
                        border: '1px solid #bfdbfe'
                    }}>
                        <div style={{ flex: 1, marginRight: '24px' }}>
                            <div style={{
                                fontWeight: '600',
                                color: '#1e40af',
                                marginBottom: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <span>📋</span>
                                <span>Müşteriye Özel Fiyatlar</span>
                            </div>
                            <div style={{ fontSize: '14px', color: '#1e3a8a', marginBottom: '12px' }}>
                                Bu müşteri için tanımlanmış özel fiyatlar bulunmaktadır.
                            </div>
                            {Object.keys(customerPrices).length > 0 && customerPrices[selectedCustomerId] && (
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '8px',
                                }}>
                                    {Object.entries(customerPrices[selectedCustomerId]).slice(0, 5).map(([productId, price]) => {
                                        const product = products.find(p => p.id === productId);
                                        return product ? (
                                            <div key={productId} style={{
                                                background: 'white',
                                                padding: '4px 10px',
                                                borderRadius: '20px',
                                                fontSize: '12px',
                                                border: '1px solid #dbeafe',
                                                color: '#1e40af',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <span style={{ fontWeight: '600' }}>{product.name}:</span>
                                                <span>{price.toFixed(2)} {currencySymbol}</span>
                                            </div>
                                        ) : null;
                                    })}
                                    {Object.keys(customerPrices[selectedCustomerId]).length > 5 && (
                                        <div style={{
                                            background: 'white',
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            border: '1px solid #dbeafe',
                                            color: '#64748b'
                                        }}>
                                            +{Object.keys(customerPrices[selectedCustomerId]).length - 5} daha fazla
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <Link
                            to={`/musteriler/${selectedCustomerId}/fiyatlar`}
                            style={{
                                background: 'white',
                                color: '#2563eb',
                                padding: '10px 16px',
                                borderRadius: '8px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                border: '1px solid #dbeafe',
                                transition: 'all 0.2s',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            <span>⚙️</span>
                            <span>Fiyatları Yönet</span>
                        </Link>
                    </div>
                )}

                <h3 style={styles.sectionTitle}>
                    <span>👤</span> Müşteri ve Sipariş Bilgileri
                </h3>

                <div style={styles.grid}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Müşteri</label>
                        <input
                            type="text"
                            placeholder="Müşteri ara..."
                            style={styles.input}
                            value={customerNameInput}
                            onChange={e => { setCustomerNameInput(e.target.value); setSelectedCustomerId(''); }}
                            required
                        />
                        {filteredCustomers.length > 0 && customerNameInput && !selectedCustomerId && (
                            <div style={styles.suggestions}>
                                {filteredCustomers.map(c => <div key={c.id} style={styles.suggestionItem} onMouseDown={() => handleCustomerSelect(c)}>{c.name}</div>)}
                            </div>
                        )}
                        {!selectedCustomerId && (
                            <div style={{
                                marginTop: '6px',
                                fontSize: '12px',
                                color: '#d97706',
                                background: '#fffbeb',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid #fef3c7',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}>
                                <span>💡</span> Önceki fiyatları görmek için bir müşteri seçin
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Sipariş Tarihi</label>
                        <input style={styles.input} type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} />
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Para Birimi</label>
                        <select style={styles.select} value={orderCurrency} onChange={e => setOrderCurrency(e.target.value)}>
                            <option value="TRY">Türk Lirası (₺)</option>
                            <option value="USD">Amerikan Doları ($)</option>
                        </select>
                    </div>
                </div>

                <div style={{ ...styles.formGroup, marginBottom: '32px' }}>
                    <label style={styles.label}>Sipariş Notu</label>
                    <textarea style={styles.textarea} value={orderNote} onChange={e => setOrderNote(e.target.value)} placeholder="Bu siparişe özel notlar..."></textarea>
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '0 0 32px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ ...styles.sectionTitle, marginBottom: 0 }}>
                        <span>📦</span> Ürünler
                    </h3>
                    {orderCurrency === 'USD' && (
                        <div style={{ fontSize: '13px', color: '#64748b', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                            Kullanılan kur: <span style={{ fontWeight: '600', color: '#0f172a' }}>1 USD = {exchangeRate} ₺</span>
                        </div>
                    )}
                </div>

                <div style={{ ...styles.grid, gridTemplateColumns: '3fr 1fr 1fr 1fr', alignItems: 'flex-end', gap: '16px' }}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Ürün Ara</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Ürün adı veya barkod..."
                                style={{ ...styles.input, paddingLeft: '36px' }}
                                value={currentItem.productName}
                                onChange={e => setCurrentItem(prev => ({ ...prev, productName: e.target.value, productId: '' }))}
                            />
                            <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5 }}>🔍</div>
                        </div>
                        {filteredProducts.length > 0 && currentItem.productName && !currentItem.productId && (
                            <div style={styles.suggestions}>
                                {filteredProducts.map(p => {
                                    const isAlreadyInOrder = lineItems.some(item => item.productId === p.id);
                                    return (
                                        <div
                                            key={p.id}
                                            style={{
                                                ...styles.suggestionItem,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                background: isAlreadyInOrder ? '#fffbeb' : 'white'
                                            }}
                                            onMouseDown={() => handleProductSelect(p)}
                                        >
                                            {p.imageUrl ? (
                                                <img
                                                    src={p.imageUrl}
                                                    alt={p.name}
                                                    style={{ width: '32px', height: '32px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                            ) : (
                                                <div style={{ width: '32px', height: '32px', background: '#f1f5f9', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>📦</div>
                                            )}
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: '500', color: '#0f172a' }}>{p.name}</div>
                                                <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#64748b' }}>
                                                    <span>Stok: {p.quantity || 0}</span>
                                                    {p.barcode && <span>• Barkod: {p.barcode}</span>}
                                                </div>
                                            </div>

                                            {(() => {
                                                const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
                                                const ep = selectedCustomerId 
                                                    ? getEffectivePrice(selectedCustomerId, p.id, p, selectedCustomer?.customerGroupId) 
                                                    : null;
                                                if (ep && ep.type !== 'default') {
                                                    return (
                                                        <div style={{ textAlign: 'right' }}>
                                                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>
                                                                {ep.price.toFixed(2)} {currencySymbol}
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: ep.type === 'product' ? '#059669' : '#06C167', opacity: 0.8 }}>{ep.label}</div>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#64748b' }}>
                                                        {(p.salePrice || 0).toFixed(2)} {currencySymbol}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Miktar</label>
                        <input
                            style={styles.input}
                            type="number"
                            min="1"
                            value={currentItem.quantity}
                            onChange={e => setCurrentItem(prev => ({ ...prev, quantity: Number(e.target.value) }))}
                        />
                        {currentItem.productId && (
                            <div style={{
                                position: 'absolute',
                                top: '-20px',
                                right: 0,
                                fontSize: '11px',
                                color: currentItem.availableStock < currentItem.quantity ? '#ef4444' : '#64748b',
                                fontWeight: currentItem.availableStock < currentItem.quantity ? '600' : '400'
                            }}>
                                Stok: {currentItem.availableStock}
                            </div>
                        )}
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>
                            Birim Fiyat ({currencySymbol})
                        </label>
                        <input
                            style={styles.input}
                            type="number"
                            value={currentItem.price}
                            onChange={e => setCurrentItem(prev => ({ ...prev, price: Number(e.target.value) }))}
                        />
                        {currentItem.isCustomPrice && (
                            <div style={{
                                position: 'absolute',
                                top: '-20px',
                                right: 0,
                                fontSize: '11px',
                                color: '#059669',
                                fontWeight: '600'
                            }}>
                                Özel Fiyat
                            </div>
                        )}
                    </div>

                    <button style={{ ...styles.button, ...styles.addButton }} onClick={handleAddItem}>
                        <span>+</span> Ekle
                    </button>
                </div>

                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Ürün</th>
                                <th style={styles.th}>Miktar</th>
                                <th style={styles.th}>Birim Fiyat</th>
                                <th style={styles.th}>Toplam</th>
                                <th style={styles.th} width="50"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                        Henüz ürün eklenmedi. Yukarıdan ürün arayıp ekleyebilirsiniz.
                                    </td>
                                </tr>
                            ) : (
                                lineItems.map((item, index) => {
                                    const product = products.find(p => p.id === item.productId);
                                    return (
                                        <tr key={index}>
                                            <td style={styles.td}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    {product?.imageUrl ? (
                                                        <img
                                                            src={product.imageUrl}
                                                            alt={item.productName}
                                                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' }}
                                                        />
                                                    ) : (
                                                        <div style={{ width: '40px', height: '40px', background: '#f1f5f9', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: '500', color: '#0f172a' }}>{item.productName}</div>
                                                        {product?.barcode && (
                                                            <div style={{ fontSize: '11px', color: '#64748b' }}>{product.barcode}</div>
                                                        )}
                                                        {item.availableStock < item.quantity && (
                                                            <div style={{ fontSize: '11px', color: '#ef4444' }}>Yetersiz Stok!</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={e => handleItemChange(index, 'quantity', e.target.value)}
                                                    style={{ ...styles.input, width: '80px', padding: '6px' }}
                                                />
                                            </td>
                                            <td style={styles.td}>
                                                <div style={{ position: 'relative' }}>
                                                    <input
                                                        type="number"
                                                        value={item.price}
                                                        onChange={e => handleItemChange(index, 'price', e.target.value)}
                                                        style={{ ...styles.input, width: '100px', padding: '6px' }}
                                                    />
                                                    {item.isCustomPrice && (
                                                        <div style={{ fontSize: '10px', color: '#059669', marginTop: '2px' }}>Özel Fiyat</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ ...styles.td, fontWeight: '600' }}>
                                                {(item.quantity * item.price).toFixed(2)} {currencySymbol}
                                            </td>
                                            <td style={styles.td}>
                                                <button style={styles.deleteButton} onClick={() => handleRemoveItem(index)}>
                                                    🗑️
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={styles.totalsContainer}>
                    <div style={styles.totalsBox}>
                        <div style={styles.totalRow}>
                            <span>Toplam Ürün:</span>
                            <span style={{ fontWeight: '600' }}>{totalProductQuantity} Adet</span>
                        </div>

                        <div style={styles.totalRow}>
                            <span>Ara Toplam:</span>
                            <span style={{ fontWeight: '600' }}>{subTotal.toFixed(2)} {currencySymbol}</span>
                        </div>

                        <div style={styles.totalRow}>
                            <span>İskonto (%):</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={discountRate}
                                    onChange={e => setDiscountRate(Number(e.target.value))}
                                    style={{ ...styles.vatInput, width: '50px' }}
                                />
                            </div>
                        </div>

                        {discountRate > 0 && (
                            <div style={{ ...styles.totalRow, color: '#ef4444', fontSize: '13px' }}>
                                <span>İskonto Tutarı:</span>
                                <span>-{discountAmount.toFixed(2)} {currencySymbol}</span>
                            </div>
                        )}

                        {discountRate > 0 && (
                            <div style={{ ...styles.totalRow, borderBottom: '1px dashed #e2e8f0', paddingBottom: '8px', marginBottom: '8px' }}>
                                <span>İndirimli Ara Toplam:</span>
                                <span style={{ fontWeight: '600' }}>{discountedSubTotal.toFixed(2)} {currencySymbol}</span>
                            </div>
                        )}

                        <div style={styles.totalRow}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>KDV</span>
                                <select
                                    style={{ ...styles.select, width: 'auto', padding: '2px 6px', fontSize: '12px' }}
                                    value={Math.round(vatRate * 100)}
                                    onChange={e => setVatRate(Number(e.target.value) / 100)}
                                >
                                    <option value="0">0%</option>
                                    <option value="1">1%</option>
                                    <option value="10">10%</option>
                                    <option value="20">20%</option>
                                </select>
                            </div>
                            <span>{vatAmount.toFixed(2)} {currencySymbol}</span>
                        </div>

                        <div style={styles.totalRow}>
                            <span>Kargo:</span>
                            <input
                                type="number"
                                value={shippingCost}
                                onChange={e => setShippingCost(e.target.value)}
                                style={styles.vatInput}
                                placeholder="0"
                            />
                        </div>

                        <div style={styles.grandTotal}>
                            <div style={{ ...styles.totalRow, marginBottom: 0 }}>
                                <span>GENEL TOPLAM:</span>
                                <span>{grandTotal.toFixed(2)} {currencySymbol}</span>
                            </div>
                            {orderCurrency === 'USD' && (
                                <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '400', marginTop: '4px', textAlign: 'right' }}>
                                    ≈ {(grandTotal * exchangeRate).toFixed(2)} ₺
                                </div>
                            )}
                        </div>

                        <button
                            style={{ ...styles.button, ...styles.primaryButton, width: '100%', marginTop: '20px', justifyContent: 'center' }}
                            onClick={handleSaveOrder}
                        >
                            <span>✓</span> Siparişi Tamamla
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default NewOrder;
